// Contracts — kids MANUAL upload (2026-06-29). A moderator who already shot on
// their phone (native camera, no in-app pipeline) ships by hand: enter the
// participant appearance + per-clip light/lux/expression, then resumable-upload
// the original clip bytes (1:1, no transcode) to the packer VPS via tus. This is
// the metadata side (kids-api / D1); the bytes go to tusd on the VPS. The session
// is created done (the packer flips `packed` once it has the clips).
import { z } from 'zod';
import { KidsGenderSchema } from '../kids';
import { KidsLightingSchema, KidsFaceExpressionSchema } from './enums';

// One clip's metadata (freeform multi-select; each clip is tagged by the mod).
export const KidsManualClipSchema = z.object({
  lighting: KidsLightingSchema,
  lux: z.number().int().min(0).max(100000),
  faceExpression: KidsFaceExpressionSchema,
  fileName: z.string().max(200).optional(), // original name, for the upload match
});
export type KidsManualClip = z.infer<typeof KidsManualClipSchema>;

// POST /api/kids/shot/manual — create the session + captures from hand-entered
// metadata. Appearance mirrors the shot AppearanceModal. Office is the caller's.
export const KidsManualSessionReqSchema = z.object({
  name: z.string().trim().min(1).max(100),
  age: z.number().int().min(5).max(17),
  gender: KidsGenderSchema,
  ethnicity: z.string(),
  skinTone: z.number().int().min(1).max(10),
  hasBeard: z.boolean(),
  hasMustache: z.boolean(),
  usesReadingGlasses: z.boolean(),
  accessoryName: z.string().max(120).nullable().optional(),
  // Exactly 4 clips, one per DISTINCT lighting — the "строго 4/4" canon (a session
  // ships with all four lights or not at all). The old 1..8 range let an API
  // caller mint a done-session with partial captures, bypassing the 4/4 gate.
  clips: z
    .array(KidsManualClipSchema)
    .length(4)
    .superRefine((clips, ctx) => {
      if (new Set(clips.map((c) => c.lighting)).size !== clips.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'duplicate lighting' });
      }
    }),
});
export type KidsManualSessionReq = z.infer<typeof KidsManualSessionReqSchema>;

export const KidsManualSessionResSchema = z.object({
  ok: z.literal(true),
  sessionId: z.number().int(),
  pid: z.string().nullable(), // moderator's 2-digit tag (clip naming on the VPS)
});
export type KidsManualSessionRes = z.infer<typeof KidsManualSessionResSchema>;

// POST /api/kids/upload/ticket — short-lived signed ticket the VPS tusd pre-create
// hook validates before accepting a resumable upload. Token is NOT put in the URL
// (secrets.md) — the client sends it in the tus metadata/header. uploadUrl is the
// tusd base (env TUSD_URL); empty when the VPS isn't wired yet.
export const KidsUploadTicketResSchema = z.object({
  ticket: z.string(),
  uploadUrl: z.string(), // tusd endpoint base ('' if not configured)
  expiresAt: z.number(),
});
export type KidsUploadTicketRes = z.infer<typeof KidsUploadTicketResSchema>;
